import { api, type Page } from '@/shared/api/client';
import {
  emailDetailKey,
  emailListsKey,
  emailNeighboursKey,
  emailsRootKey,
  emailViewsKey,
  sharedEmailsApi,
} from '@/shared/emails/api';
import type { EmailCategory } from '@/shared/triage/vocabulary';

import type {
  AttachmentUrl,
  Email,
  EmailDetail,
  EmailNeighbours,
  EmailStatus,
  InboxView,
  InboxViews,
  IngestStats,
  ReadAllResult,
} from './types';

/** `email` is what came through a mailbox, `manual` what was uploaded from a quote. */
export type EmailOrigin = 'email' | 'manual';

/** `all` is the API's default: the inbox plus what looks legitimate in spam. */
export type SpamView = 'all' | 'spam' | 'inbox';

export interface EmailListParams {
  mailbox?: string;
  origin?: EmailOrigin;
  status?: EmailStatus;
  /** Sent as `in_spam`: `spam` → true, `inbox` → false, `all` → omitted. */
  spam?: SpamView;
  /**
   * Words, all of which have to appear in the sender, the subject, the body or an
   * attachment name (contract v10.21). Quotes make a phrase, a dash excludes. The API
   * answers each row with `match_snippet` when there is one.
   */
  search?: string;
  /** «Qué es»: the category of the live decision. The API takes it repeated; the screen
   * sends one. */
  category?: EmailCategory;
  /**
   * Whether the person asking has opened it in the platform (contract v10.22): `false`
   * is «No leídos». Each person's own, and never the mailbox's `Seen`.
   */
  read?: boolean;
  /**
   * Where to look (contract v10.25): «Por revisar», a folder (with `folder`), «Todos»,
   * the trash, the server's spam folder or the ones in error. Without it, the inbox as
   * it was before v10.25.
   */
  view?: InboxView;
  /** The folder of a `carpeta` view, as the panel names it («Pedidos cliente»). */
  folder?: string;
  limit?: number;
  offset?: number;
}

function wireParams({ spam, ...rest }: EmailListParams) {
  return { ...rest, in_spam: spam === 'spam' ? true : spam === 'inbox' ? false : undefined };
}

export const emailKeys = {
  all: emailsRootKey,
  lists: emailListsKey,
  list: (params: EmailListParams) => ['emails', 'list', params] as const,
  mailboxes: ['emails', 'mailboxes'] as const,
  views: (mailbox: string) => [...emailViewsKey, mailbox] as const,
  allViews: emailViewsKey,
  neighbours: (id: string, params: EmailListParams) => [...emailNeighboursKey, id, params] as const,
  allNeighbours: emailNeighboursKey,
  ingest: ['emails', 'ingest'] as const,
  // The same key `@/shared/emails` uses: the quote editor reads and requeues an
  // email through it, and one cache entry has to answer to both.
  detail: emailDetailKey,
};

export const emailsApi = {
  list: (params: EmailListParams = {}): Promise<Page<Email>> =>
    api.get<Page<Email>>('/api/emails', wireParams(params)),

  /** Mailboxes that have mail. Three at most, so the first page is all of them. */
  mailboxes: (): Promise<Page<string>> =>
    api.get<Page<string>>('/api/emails/mailboxes', { limit: 200 }),

  detail: sharedEmailsApi.detail,

  /**
   * How the mail is coming in, per mailbox: rows by status and the connector's last
   * heartbeat. The inbox uses it for the counts on the filter and for the strip that
   * says "the reader has gone quiet", which is the one thing worth knowing before
   * wondering why a mail is not here.
   */
  ingest: (): Promise<IngestStats> => api.get<IngestStats>('/api/stats/ingest'),

  /** Back to `pending` and into the queue again. Valid from any status. */
  reprocess: sharedEmailsApi.reprocess,

  /** Short lived download link straight to the bucket; the bytes never pass through the API. */
  attachmentUrl: (emailId: string, attachmentId: string): Promise<AttachmentUrl> =>
    api.get<AttachmentUrl>(`/api/emails/${emailId}/attachments/${attachmentId}/url`),

  /**
   * Read and unread, for the person asking and only in the platform (contract v10.22).
   * Both idempotent. The detail marks with the PUT once when it opens; the GET of the
   * detail never marks, so «Marcar como no leído» is not undone by the next refetch.
   */
  markRead: (emailId: string): Promise<void> =>
    api.put<void>(`/api/emails/${emailId}/read`, undefined),
  markUnread: (emailId: string): Promise<void> => api.delete<void>(`/api/emails/${emailId}/read`),

  /**
   * The rail in one request (contract v10.25): every view and every folder, with how many
   * emails and how many the person has not opened. For one mailbox, or all they see.
   */
  views: (mailbox = ''): Promise<InboxViews> =>
    api.get<InboxViews>('/api/emails/views', mailbox ? { mailbox } : {}),

  /** The email above and below this one in a list with these filters, newest first. */
  neighbours: (
    id: string,
    { limit: _limit, offset: _offset, ...filters }: EmailListParams = {},
  ): Promise<EmailNeighbours> =>
    api.get<EmailNeighbours>(`/api/emails/${id}/neighbours`, wireParams(filters)),

  /**
   * «Eliminar»: to the trash of the mailbox, never deleted from the server; «Recuperar»
   * brings it back to where it was. «Devolver a la bandeja» undoes «Correcto»: the
   * original back in the inbox, unread, and the email back in «Por revisar». The three
   * are done by the API on the spot and answer with the email as it is now.
   */
  trash: (id: string): Promise<EmailDetail> => api.post<EmailDetail>(`/api/emails/${id}/trash`),
  untrash: (id: string): Promise<EmailDetail> => api.delete<EmailDetail>(`/api/emails/${id}/trash`),
  returnToInbox: (id: string): Promise<EmailDetail> =>
    api.post<EmailDetail>(`/api/emails/${id}/return-to-inbox`),

  /**
   * «Marcar todo como leído»: every page of what the list shows with these filters, never
   * more. `read`, `limit` and `offset` are the list's own and do not travel.
   */
  readAll: ({ read: _read, limit: _limit, offset: _offset, ...filters }: EmailListParams = {}) =>
    api.post<ReadAllResult>('/api/emails/read-all', undefined, wireParams(filters)),
};
